import { createClient, isAuthRetryableFetchError, type Session } from "@supabase/supabase-js";
import { env } from "./env";

/**
 * Why an emailed sign-in link failed — used, expired, or replaced by a newer one.
 * Supabase still sends the buyer to the link's page with the reason in the URL
 * hash, the route guard then bounces them to the login page, and that redirect
 * drops the hash: the buyer just sees the login page again with no idea why.
 * Read here, at import, before anything can navigate.
 */
let authLinkError: string | null = (() => {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.hash.slice(1));
  return params.get("error_code") ?? params.get("error");
})();

export function peekAuthLinkError(): string | null {
  return authLinkError;
}

export function clearAuthLinkError(): void {
  authLinkError = null;
}

/** Browser Supabase client — persists the session in localStorage. */
export const supabase = createClient(env.supabaseUrl, env.supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // Supabase ships passkeys behind this flag; the API may change between
    // supabase-js releases, so re-run the passkey e2e after every upgrade.
    experimental: { passkey: true },
  },
});

const SESSION_TIMEOUT_MS = 5000;
/**
 * Once a getSession() has been seen to stall, every later call is answered with
 * `null` immediately for this long. Without the latch each of the ~6 requests the
 * dashboard fires on load pays the full timeout on its own, so a poisoned token
 * still costs tens of seconds of spinner before anything decides to bounce to
 * /login. The latch is released the moment the stalled call finally resolves with
 * a real session, so a merely slow refresh self-heals.
 */
const STALL_COOLDOWN_MS = 15_000;

const STALLED = Symbol("stalled");

/**
 * How long a refresh that failed on the network (phone waking up, a dropped
 * connection) counts as "unknown" rather than "signed out". supabase-js keeps
 * the session in storage after such a failure and retries; treating the null it
 * hands back as a logout is what bounced people to /login over a flaky signal.
 */
const OFFLINE_GRACE_MS = 15_000;

let inFlightSession: Promise<Session | null> | null = null;
let stalledUntil = 0;
let offlineUntil = 0;

function sharedGetSession(): Promise<Session | null> {
  if (!inFlightSession) {
    inFlightSession = supabase.auth
      .getSession()
      .then(({ data, error }) => {
        if (data.session) offlineUntil = 0;
        else if (error && isAuthRetryableFetchError(error)) offlineUntil = Date.now() + OFFLINE_GRACE_MS;
        return data.session;
      })
      .catch(() => {
        offlineUntil = Date.now() + OFFLINE_GRACE_MS;
        return null;
      })
      .finally(() => {
        inFlightSession = null;
      });
  }
  return inFlightSession;
}

/**
 * `getSession()` that can never hang. supabase-js holds an internal auth lock
 * while it refreshes an expired access token; if that refresh stalls (flaky
 * network, a lock left held by a prior operation), a bare `getSession()` blocks
 * forever. Because we call it at the top of EVERY authenticated request and at
 * app boot, one stalled refresh would freeze the whole dashboard on its loading
 * spinner — the request never even leaves the browser. Cap the wait and answer
 * `null`; callers check `sessionUncertain()` to tell "no session" from "could
 * not tell yet".
 *
 * Concurrent callers share ONE underlying getSession() — the dashboard fires its
 * requests in a burst, and piling separate calls onto the same auth lock is what
 * makes a stall more likely in the first place.
 */
export async function getSessionSafe(timeoutMs = SESSION_TIMEOUT_MS): Promise<Session | null> {
  if (Date.now() < stalledUntil) return null;

  const pending = sharedGetSession();

  let timer: ReturnType<typeof setTimeout> | undefined;
  const result = await Promise.race([
    pending,
    new Promise<typeof STALLED>((resolve) => {
      timer = setTimeout(() => resolve(STALLED), timeoutMs);
    }),
  ]);
  if (timer !== undefined) clearTimeout(timer);
  if (result !== STALLED) return result;

  stalledUntil = Date.now() + STALL_COOLDOWN_MS;
  void pending.then((late) => {
    if (late) stalledUntil = 0;
  });
  return null;
}

/**
 * Why the last `getSessionSafe()` answered null without meaning "signed out":
 * "stalled" — the refresh is still holding the auth lock; "offline" — it failed
 * on the network and supabase-js still holds the session. null when a null
 * session really is a signed-out user.
 */
export function sessionUncertain(): "stalled" | "offline" | null {
  const now = Date.now();
  if (now < stalledUntil) return "stalled";
  if (now < offlineUntil) return "offline";
  return null;
}

/**
 * Wait up to `timeoutMs` for a session, ignoring the stall latch: joins the
 * refresh still in flight, or starts a fresh attempt when the last one failed on
 * the network. A slow refresh lands well inside this; only a genuinely poisoned
 * lock outlasts it.
 */
export async function waitForSession(timeoutMs: number): Promise<Session | null> {
  const pending = sharedGetSession();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const result = await Promise.race([
    pending,
    new Promise<typeof STALLED>((resolve) => {
      timer = setTimeout(() => resolve(STALLED), timeoutMs);
    }),
  ]);
  if (timer !== undefined) clearTimeout(timer);
  if (result === STALLED) {
    // Still holding the lock after the whole wait: keep calling it stalled, so
    // the caller can tell this poisoned case from an offline one.
    stalledUntil = Date.now() + STALL_COOLDOWN_MS;
    return null;
  }
  if (result) stalledUntil = 0;
  return result;
}

/**
 * Drop the persisted session. A stalled refresh leaves the poisoned token sitting
 * in localStorage, where it survives a reload and re-hangs `getSession()` on the
 * next boot — so a "bounce to /login" that only navigates would land the user on
 * a login page that stalls exactly the same way. Removing the key is what makes
 * the recovery stick without Clear-Site-Data / Incognito.
 */
export function clearStoredSession(): void {
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith("sb-") && key.includes("auth-token")) localStorage.removeItem(key);
    }
  } catch {
    /* private mode / quota — nothing to clean up */
  }
  inFlightSession = null;
  stalledUntil = 0;
  offlineUntil = 0;
}

/**
 * `signOut()` that can never hang. It takes the same auth lock `getSession()`
 * does and posts to /logout, so on a poisoned token it blocks indefinitely — and
 * anything awaiting it (the 401 → /login bounce) never runs. Cap the wait, then
 * clear the stored session ourselves so the sign-out is effective either way.
 *
 * "local" by default: signing out ends THIS device only. "global" revokes every
 * session the user has, so as a default one tap on a phone — or one automatic
 * 401 bounce — logged them out of their laptop too. Only the explicit "sign out
 * of all devices" control passes "global".
 */
export async function signOutSafe(timeoutMs = 2000, scope: "global" | "local" = "local"): Promise<void> {
  try {
    await Promise.race([
      supabase.auth.signOut({ scope }),
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  } catch {
    /* ignore — we're leaving anyway */
  }
  clearStoredSession();
}
